// iot-admin/client/src/components/ConnectionStatusBadge.js
// Reusable badge for displaying GGOS device connection status

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ConnectionStatusBadge.css';

const MINS_IN_HOUR = 60;
const HOURS_IN_DAY = 24;

const STATUS_CONFIG = {
  online: {
    icon: '🟢',
    label: 'Online',
    color: 'green',
    description: 'Device is connected',
  },
  offline: {
    icon: '🔴',
    label: 'Offline',
    color: 'red',
    description: 'Device is disconnected',
  },
  unknown: {
    icon: '⚪',
    label: 'Unknown',
    color: 'gray',
    description: 'Connection status unknown',
  },
};

const ConnectionStatusBadge = ({
  status,
  lastConnectedAt,
  showLabel = true,
  size = 'medium',
  autoRefresh = false,
  onStatusChange,
}) => {
  const [animating, setAnimating] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(status);

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;

  // Animate on status change
  useEffect(() => {
    if (status !== previousStatus) {
      setAnimating(true);
      setPreviousStatus(status);
      if (onStatusChange) {
        onStatusChange(status, previousStatus);
      }
      const timer = setTimeout(() => setAnimating(false), 500);
      return () => clearTimeout(timer);
    }
  }, [status, previousStatus, onStatusChange]);

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return null;
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < MINS_IN_HOUR) return `${diffMins}m ago`;
    if (diffHours < HOURS_IN_DAY) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const relativeTime = formatRelativeTime(lastConnectedAt);
  const title = `${config.description}${relativeTime ? ` | Last seen: ${relativeTime}` : ''}`;

  return (
    <span
      className={`connection-badge connection-badge--${size} connection-badge--${config.color}${animating ? ' connection-badge--animating' : ''}`}
      title={title}
    >
      <span className="connection-badge__icon">{config.icon}</span>
      {showLabel && (
        <span className="connection-badge__label">{config.label}</span>
      )}
      {autoRefresh && (
        <span className="connection-badge__refresh-indicator" title="Auto-refreshing">↻</span>
      )}
    </span>
  );
};

ConnectionStatusBadge.propTypes = {
  status: PropTypes.oneOf(['online', 'offline', 'unknown']).isRequired,
  lastConnectedAt: PropTypes.string,
  showLabel: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  autoRefresh: PropTypes.bool,
  onStatusChange: PropTypes.func,
};

export default ConnectionStatusBadge;
